import React, { useCallback, useMemo } from 'react'

import { Node, mergeAttributes } from '@tiptap/core'
import {
  NodeViewWrapper,
  type ReactNodeViewProps,
  ReactNodeViewRenderer,
} from '@tiptap/react'

import { useChatCitationInteraction } from '@/extensions/ChatCitation/context'
import {
  CHAT_CITATION_NODE_NAME,
  decodeChatCitationSources,
  encodeChatCitationSources,
} from '@/extensions/ChatCitation/lib'

import { cleanFileName } from '@/helpers/file'
import { cn } from '@/helpers/shadcn/utils'

import type { Source } from '@/types/chat-v2'

declare module '@tiptap/core' {
  interface Commands<ReturnType> {
    chatCitation: {
      insertChatCitation: (sources: Source[]) => ReturnType
    }
  }
}

const getCitationDisplayName = (
  source: Source | null | undefined,
  fileName: string | null | undefined,
) => {
  if (!source) return null

  switch (source.source_type) {
    case 'file':
      return cleanFileName(fileName ?? source.file_name ?? 'Document')
    case 'web':
      return source.url
        .replace(/^https?:\/\//, '')
        .replace(/^www\./, '')
        .split('/')[0]
    case 'tool':
      return source.tool_display_name ?? source.tool_name
    case 'pursuit':
      return source.pursuit_title
    case 'explore':
      return source.title ?? source.question ?? 'Search'
    case 'entity':
      return source.entity_type
    case 'image':
      return source.prompt ?? 'Generated image'
    default: {
      const _exhaustive: never = source
      void _exhaustive
      return null
    }
  }
}

const reorderSourcesForOpen = (sources: Source[]) => {
  const fileSources = sources.filter(
    (source): source is Extract<Source, { source_type: 'file' }> =>
      source.source_type === 'file',
  )

  if (fileSources.length === 0) {
    return sources
  }

  return [
    ...fileSources,
    ...sources.filter((source) => source.source_type !== 'file'),
  ]
}

interface ChatCitationChipProps extends Omit<
  React.ButtonHTMLAttributes<HTMLButtonElement>,
  'children'
> {
  sources: Source[]
}

const ChatCitationChip: React.FC<ChatCitationChipProps> = ({
  sources,
  className,
  onClick,
  ...props
}) => {
  const interaction = useChatCitationInteraction()
  const firstSource = sources[0]
  const firstFileName =
    firstSource?.source_type === 'file'
      ? (interaction?.filesById.get(firstSource.file_id)?.name ?? null)
      : null
  const displayName =
    getCitationDisplayName(firstSource, firstFileName) ?? 'Citation'
  const remainingCount = Math.max(sources.length - 1, 0)

  const handleClick = useCallback(() => {
    const orderedSources = reorderSourcesForOpen(sources)
    if (orderedSources[0] != null && orderedSources[0].source_type === 'file') {
      interaction?.openSources(orderedSources, 'citations')
      return
    }

    if (!firstSource) {
      return
    }

    switch (firstSource.source_type) {
      case 'web': {
        const win = window.open(
          firstSource.url,
          '_blank',
          'noopener,noreferrer',
        )
        if (win) win.opener = null
        return
      }
      case 'file':
        return
      case 'entity':
      case 'image':
      case 'tool':
      case 'pursuit':
      case 'explore':
        return
      default: {
        const _exhaustive: never = firstSource
        void _exhaustive
      }
    }
  }, [firstSource, interaction, sources])

  return (
    <button
      type="button"
      className={cn(
        'inline-flex max-w-full items-center justify-center gap-1 rounded-full',
        'h-[17px] px-[5px] py-[1px] text-[11px] font-medium',
        'bg-primary-200 text-primary-700 ring-1 ring-primary-300 shadow-sm',
        'transition-colors duration-200 hover:bg-primary-300/80 hover:text-primary-900',
        'dark:bg-primary-800 dark:text-primary-300 dark:ring-primary-700 dark:shadow-primary-900/20 dark:hover:bg-primary-700/80 dark:hover:text-primary-100',
        'cursor-pointer align-middle',
        className,
      )}
      aria-label={displayName}
      onClick={(event) => {
        event.preventDefault()
        event.stopPropagation()
        onClick?.(event)
        handleClick()
      }}
      {...props}
    >
      <span className="truncate max-w-[84px]">{displayName}</span>
      {remainingCount > 0 && (
        <span className="flex-shrink-0">+{remainingCount}</span>
      )}
    </button>
  )
}

ChatCitationChip.displayName = 'ChatCitation.Chip'

const ChatCitationNodeView: React.FC<ReactNodeViewProps> = ({ node }) => {
  const sources = useMemo(() => {
    return Array.isArray(node.attrs.sources)
      ? (node.attrs.sources as Source[])
      : []
  }, [node.attrs.sources])

  if (sources.length === 0) {
    return null
  }

  return (
    <NodeViewWrapper
      as="span"
      className="inline-block align-top"
      contentEditable={false}
    >
      <ChatCitationChip sources={sources} />
    </NodeViewWrapper>
  )
}

ChatCitationNodeView.displayName = 'ChatCitation.NodeView'

export interface ChatCitationOptions {
  HTMLAttributes: Record<string, unknown>
}

export const ChatCitation = Node.create<ChatCitationOptions>({
  name: CHAT_CITATION_NODE_NAME,

  inline: true,
  group: 'inline',
  atom: true,
  selectable: false,
  draggable: false,

  addOptions() {
    return {
      HTMLAttributes: {},
    }
  },

  addAttributes() {
    return {
      sources: {
        default: [],
        parseHTML: (element: HTMLElement) =>
          decodeChatCitationSources(element.getAttribute('data-sources')),
        renderHTML: (attributes: { sources?: Source[] }) => ({
          'data-sources': encodeChatCitationSources(attributes.sources ?? []),
        }),
      },
    }
  },

  parseHTML() {
    return [
      {
        tag: 'chat-citation',
      },
      {
        tag: "span[data-type='chat-citation']",
      },
    ]
  },

  renderHTML({ HTMLAttributes }) {
    return [
      'chat-citation',
      mergeAttributes(
        { 'data-type': 'chat-citation' },
        this.options.HTMLAttributes,
        HTMLAttributes,
      ),
    ]
  },

  addCommands() {
    return {
      insertChatCitation:
        (sources: Source[]) =>
        ({ commands }) => {
          return commands.insertContent({
            type: this.name,
            attrs: {
              sources,
            },
          })
        },
    }
  },

  addNodeView() {
    return ReactNodeViewRenderer(ChatCitationNodeView)
  },
})

export default ChatCitation
